import AnalyticsEvent = ToroAnalyticsEnums.AnalyticsEvent;

import { Component, OnInit } from '@angular/core';
import { finalize, take } from 'rxjs/operators';
import { I3AssetDown, I3AssetsDown } from '../../../../../api/intelli360/models/i3-assets-down.model';
import { AnalyticsService } from '../../../../../common/services/analytics.service';
import { AuthManagerService } from '../../../../../api/auth/auth-manager.service';
import { BroadcastService } from '../../../../../common/services/broadcast.service';
import { DashMessageService } from '../../../../../common/services/dash-message.service';
import { DashUserManagerService } from '../../../../../api/dash-user/dash-user-manager.service';
import { DeviceManagerService } from '../../../../../common/services/device-manager.service';
import { environment } from '../../../../../../environments/environment';
import { HttpStatusCode } from '@angular/common/http';
import { Intelli360ManagerService } from '../../../../../api/intelli360/intelli360-manager.service';
import { Intelli360WidgetBase } from '../_intelli360-widget-base';
import { ToroAnalyticsEnums } from '../../../../../common/enumerations/analytics.enums';
import { TranslateService } from '@ngx-translate/core';

@Component({
    selector: 'toro-widget-intelli360-asset-down',
    templateUrl: './widget-intelli360-asset-down.component.html',
    styleUrls: ['./widget-intelli360-asset-down.component.less']
})
export class WidgetIntelli360AssetDownComponent extends Intelli360WidgetBase implements OnInit {

    iconColor = 'black';
    title = 'WIDGET.INTELLI360_ASSETS_DOWN';

    protected showDesktopModal = false;
    protected isDemoMode = false;
    protected assets: I3AssetsDown;
    protected selectedAsset: I3AssetDown;

    // =========================================================================================================================================================
    // Ctor and Lifecycle Hooks
    // =========================================================================================================================================================

    constructor(protected analyticsService: AnalyticsService,
                private authManager: AuthManagerService,
                protected broadcastService: BroadcastService,
                protected dashMessageService: DashMessageService,
                protected dashUserManager: DashUserManagerService,
                protected deviceManager: DeviceManagerService,
                private intelli360Manager: Intelli360ManagerService,
                protected translateService: TranslateService,
    ) {
        super(analyticsService, broadcastService, dashUserManager, deviceManager, intelli360Manager, translateService);
    }

    ngOnInit(): void {
        super.ngOnInit();

        this.isDemoMode = environment.isDemoMode;
        if (this.isDemoMode) { this.title = ''; }

        // NOTE: Initial data is fetched in the base class call to super.widgetResized. This is to ensure we don't attempt to load
        // any widget content until the widget has been properly sized (i.e., displayCols/displayRows has been properly set.
    }

    // =========================================================================================================================================================
    // Base Class Overrides
    // =========================================================================================================================================================

    get analyticsWidgetName(): string {
        return AnalyticsEvent.Intelli360AssetDownWidgetName;
    }

    protected getWidgetData() {
        if (this.isDemoMode) {
            // In demo mode, we do not fetch real data. We show an image.
            this.isBusy = false;
            return;
        }

        if (!this.isIntelli360UserLoggedIn) { return; }
        if (this.authManager.isDoingIntelli360Login) { return; }
        if (this.isUnableToFetchData) { this.isBusy = true; }

        this.intelli360Manager.getAssetsDown()
            .pipe(
                take(1),
                finalize(() => {
                    if (this.selectedAsset == null && this.assets?.value?.length > 0) { this.selectedAsset = this.assets?.value[0]; }
                    this.isWidgetInitialized = true;
                    this.isBusy = false;
                })
            )
            .subscribe({
                next: (response: I3AssetsDown) => {
                     this.assets = response;

                     if (!this.assets || !this.assets.value || this.assets.value.length < 1) { this.selectedAsset = null; }

                    this.lastUpdateTimestamp = new Date();
                    this.clearIsUnableToFetchData();
                }, error: err => {
                    this.isIntelli360UserLoggedIn = err.status !== HttpStatusCode.FailedDependency;
                    if (!this.isIntelli360UserLoggedIn) { this.intelli360UserName = ''; }

                    let errorMessage = !this.isIntelli360UserLoggedIn ? 'ERR_MSG.I3_USER_NOT_LOGGED_IN' : 'ERR_MSG.SERVER_ERROR';

                    // Check for bad Global Identifier
                    const error1 = err?.error?.Error_1?.[0];
                    if (error1?.includes('GlobalIdentifier') && error1?.includes('not found') ) {
                        errorMessage = 'No data found for logged in Intelli360 user.'
                    }

                    this.setIsUnableToFetchData(errorMessage);
                    if (this.isWidgetInitialized) { this.dashMessageService.showWidgetDataFetchErrorMessage(this.title); }
                }
            });
    }

    // =========================================================================================================================================================
    // Event Handlers
    // =========================================================================================================================================================

    onLinkClick() {
        this.broadcastService.toggleSystemOverlay.next({ show: true, text: 'STRINGS.NAVIGATING_TO_INTELLI360' });
        setTimeout(() => window.open(environment.intelli360SiteUrl, '_blank'), 1000);
        setTimeout(() => this.broadcastService.toggleSystemOverlay.next({ show: false }), 2000);
    }

}
